import {makeStyles, Theme} from "@material-ui/core";

type Props = {
  comment: string;
};
const useStyles = makeStyles<Theme, Props>((theme: Theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
    "& >img": {
      width: 80,
      height: 80,
    },
  },
  container: {
    paddingTop: 24,
    paddingBottom: 48,
    maxWidth: 1400,
    margin: "0 auto",
    paddingLeft: 24,
    paddingRight: 24,
    boxSizing: "border-box",
    [theme.breakpoints?.down("sm")]: {
      paddingTop: 16,
      paddingBottom: 24,
      paddingLeft: 12,
      paddingRight: 12,
    },
  },
  backButton: {
    marginBottom: 20,
    color: "#475569",
    fontWeight: 700,
    fontSize: "0.875rem",
    textTransform: "none",
    borderRadius: 10,
    padding: "6px 14px",
    backgroundColor: "#F8FAFC",
    border: "1px solid #E2E8F0",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#F1F5F9",
      color: "#0F172A",
      transform: "translateX(-2px)",
    },
  },
  playerFrame: {
    borderRadius: 24,
    overflow: "hidden",
    backgroundColor: "#0F172A",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.3)",
    marginBottom: 24,
    position: "relative",
  },
  playerWrapper: {
    position: "relative",
    paddingTop: "56.25%", /* 16:9 Aspect Ratio */
  },
  reactPlayer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  playIconBtn: {
    fontSize: 64,
    color: "#FFFFFF",
    backgroundColor: "#4F46E5",
    borderRadius: "50%",
    padding: 12,
    boxShadow: "0 8px 24px rgba(79, 70, 229, 0.5)",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },

  // Lesson Details Card
  infoCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 28,
    border: "1px solid #E2E8F0",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
    marginBottom: 24,
    [theme.breakpoints?.down("sm")]: {
      padding: 18,
      borderRadius: 16,
    },
  },
  categoryChip: {
    backgroundColor: "#EEF2FF",
    color: "#4F46E5",
    fontWeight: 900,
    fontSize: "0.775rem",
    padding: "4px 12px",
    borderRadius: 8,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  lessonTitle: {
    fontSize: "1.65rem",
    fontWeight: 900,
    color: "#0F172A",
    lineHeight: 1.35,
    marginTop: 12,
    marginBottom: 12,
    [theme.breakpoints?.down("sm")]: {
      fontSize: "1.2rem",
    },
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: "1px solid #F1F5F9",
  },
  dateText: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "#64748B",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  descTitle: {
    fontSize: "0.95rem",
    fontWeight: 800,
    color: "#0F172A",
    marginBottom: 6,
  },
  descContent: {
    fontSize: "0.925rem",
    color: "#475569",
    lineHeight: 1.6,
  },

  // Comments Section
  commentSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 28,
    border: "1px solid #E2E8F0",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
    [theme.breakpoints?.down("sm")]: {
      padding: 18,
    },
  },
  showMoreBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    color: "#4F46E5",
    fontWeight: 800,
    cursor: "pointer",
    padding: "10px 0",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#3730A3",
    },
  },
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  icon: {
    width: 20,
    height: 20,
    objectFit: "cover",
    marginRight: 8,
    cursor: "pointer",
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
    border: "2px solid #E2E8F0",
  },
  avatar__reply: {
    width: 32,
    height: 32,
  },
  username: {
    fontWeight: 700,
    fontSize: "0.875rem",
    color: "#0F172A",
    "&>span": {
      fontSize: "0.75rem",
      marginLeft: 8,
      color: "#94A3B8",
      fontWeight: 500,
    },
  },
  reply: {
    cursor: "pointer",
    color: "#64748B",
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  btn__comment: {
    background: (props) =>
      props.comment.length > 0
        ? "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)"
        : "#CBD5E1",
    color: "#fff",
    borderRadius: 10,
    fontWeight: 700,
    padding: "6px 20px",
  },
  icon_emoji: {
    position: "absolute",
    bottom: 7,
    right: 10,
    "& >span >img": {
      width: 25,
      height: 25,
    },
  },
  icon_play: {
    padding: 5,
    backgroundColor: "#fff",
    width: 50,
    height: 50,
    borderRadius: "50%",
    color: "#f9ab00",
    transition: "ease-in 0.5s",
    border: "8px solid #999",
    "&:hover": {
      backgroundColor: "#f9ab00",
      color: "#fff",
    },
  },
  bg_lesson: {
    padding: 15,
    paddingRight: 0,
    "& >img": {
      width: "100%",
      height: "100%",
    },
  },
  btn_delete: {
    borderRadius: 10,
    backgroundColor: "#FEF2F2",
    color: "#DC2626",
    fontWeight: 700,
    padding: "6px 14px",
    textTransform: "none",
    border: "1px solid #FCA5A5",
    "&:hover": {
      backgroundColor: "#FEE2E2",
    },
  },
}));

export default useStyles;
